import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import {
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  bodyParts,
  CodedEntry,
  DEFAULT_PART_HEIGHT,
  DEFAULT_PART_WIDTH,
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
  Position,
  positionMap,
} from '../../../../shared/utils/bodyPart';
import bodyImage from '../../assets/body.jpg';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  padding: 10px;
`;

const BodyImage = styled.img`
  width: 100%;
`;

const Spot = styled.div<Position & { selected?: boolean; hover?: boolean }>`
  z-index: 1;
  position: absolute;
  left: ${p => (p.left / IMAGE_WIDTH) * 100}%;
  top: ${p => (p.top / IMAGE_HEIGHT) * 100}%;
  width: ${p => ((p.width ?? DEFAULT_PART_WIDTH) / IMAGE_WIDTH) * 100}%;
  height: ${p => ((p.height ?? DEFAULT_PART_HEIGHT) / IMAGE_HEIGHT) * 100}%;
  /*
  left: ${p => `calc(${p.left} / ${IMAGE_WIDTH} * 100%)`};
  top: ${p => `calc(${p.top} / ${IMAGE_HEIGHT} * 100%)`};
  width: ${p =>
    `calc(${p.width ?? DEFAULT_PART_WIDTH} / ${IMAGE_WIDTH} * 100%)`};
  height: ${p =>
    `calc(${p.height ?? DEFAULT_PART_HEIGHT} / ${IMAGE_HEIGHT} * 100%)`};
  */
  border-radius: 20px;
  background-color: ${p =>
    !p.selected && !p.hover ? 'transparent' : p.theme.colors.request};
  opacity: ${p => (p.selected ? 0.7 : 0.3)};
  z-index: ${p => p.zIndex ?? 0};
  cursor: pointer;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 60%;
`;

const SearchListContainerWrapper = styled.div`
  position: relative;
  margin-left: 10px;
  width: 40%;
`;

const SearchListContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;

  position: absolute;
  top: 0;
  max-height: 100%;
  width: 100%;
`;

const BodyPartList = styled(List)`
  overflow: auto;
`;

const BodyPartListItem = styled(ListItem)<{ selected?: boolean }>`
  background-color: ${p =>
    p.selected ? p.theme.colors.request : p.theme.colors.white};
  cursor: pointer;
`;

export interface BodyPartSelectorProps {
  code?: CodedEntry;
  setCode: (c?: CodedEntry) => void;
}
export const BodyPartSelector: React.FC<BodyPartSelectorProps> = (
  props: BodyPartSelectorProps,
) => {
  const { code, setCode } = props;
  const [hover, setHover] = useState<CodedEntry>();
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const filtered = useMemo(
    () =>
      bodyParts.filter(
        part =>
          part.meaning.toLowerCase().startsWith(text) ||
          `${t(`common.bodyPart.${part.meaning}`)}`.startsWith(text),
      ),
    [t, text],
  );
  return (
    <Container>
      <ImageContainer>
        <BodyImage src={bodyImage} />
        {bodyParts.map(part => {
          const positions = positionMap[part.meaning];
          return (Array.isArray(positions) ? positions : [positions]).map(
            (position, index) => (
              <Spot
                {...position}
                key={`${part.value}@${index}`}
                selected={code?.meaning === part.meaning}
                hover={hover?.meaning === part.meaning}
                onMouseEnter={() => setHover(part)}
                onMouseLeave={() => setHover(undefined)}
                onClick={() => setCode(part)}
              />
            ),
          );
        })}
      </ImageContainer>
      <SearchListContainerWrapper>
        <SearchListContainer>
          <TextField
            value={text}
            onChange={e => setText(e.target.value)}
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterAltIcon />
                </InputAdornment>
              ),
            }}
          />
          <BodyPartList>
            {filtered.map(part => (
              <BodyPartListItem
                selected={
                  code?.meaning === part.meaning ||
                  hover?.meaning === part.meaning
                }
                key={part.meaning}
                onClick={() => setCode(part)}
                onMouseEnter={() => setHover(part)}
                onMouseLeave={() => setHover(undefined)}>
                <ListItemText>
                  {t(`common.bodyPart.${part.meaning}`)}
                  {`(${part.meaning})`}
                </ListItemText>
              </BodyPartListItem>
            ))}
          </BodyPartList>
        </SearchListContainer>
      </SearchListContainerWrapper>
    </Container>
  );
};
